import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { GRID_PAGE_SIZE } from '@models-global/constants';
import { ModelPagination } from '@models-global/model-pagination';
import { HttpResp } from '@models-global/http-response';
import {
  IReportDownload,
  IReportDownloadListRequest,
  IReportGenerateRequest
} from '../models/report-download.interface';
import { environment } from '@environment/environment';
import { format } from 'date-fns';
import { getISODateString } from '@utils-global/date-time';

@Injectable()
export class ReportHttpService {
  private readonly apiReport = `${environment.api}/reports`;

  constructor(private http: HttpClient) {}

  getReportDownloadList({
    entityId,
    rebateCycle,
    sort = [],
    skip = 0,
    take = GRID_PAGE_SIZE
  }: IReportDownloadListRequest): Observable<
    ModelPagination<IReportDownload[]>
  > {
    let params = new HttpParams();
    params = params.append('skip', skip);
    params = params.append('take', take);
    params = params.append('sortBy', sort[0]?.field || 'createdAt');
    params = params.append('orderBy', sort[0]?.dir || 'desc');

    if (rebateCycle) {
      params = params.append('rebateCycle', format(rebateCycle, 'yyyy-MM-dd'));
    }

    return this.http
      .get<HttpResp<ModelPagination<IReportDownload[]>>>(
        `${this.apiReport}/all/${entityId}`,
        {
          params
        }
      )
      .pipe(
        map(({ data }) => ({
          skip,
          take,
          total: data.total,
          data: data.data
        }))
      );
  }

  generateReport({
    entityId,
    reportType,
    claimStatus,
    invoiceStatus,
    startDate,
    endDate
  }: IReportGenerateRequest): Observable<IReportDownload> {
    const body = {
      entityId,
      reportType,
      claimStatus,
      ...(invoiceStatus ? { invoiceStatus } : {}),
      ...(startDate ? { startDate: getISODateString(startDate) } : {}),
      ...(endDate ? { endDate: getISODateString(endDate) } : {})
    };

    return this.http
      .post<HttpResp<IReportDownload>>(this.apiReport, body)
      .pipe(map(({ data }) => data));
  }

  downloadGeneratedReport(reportId: string): Observable<IReportDownload> {
    return this.http
      .get<HttpResp<IReportDownload>>(`${this.apiReport}/${reportId}`)
      .pipe(
        map(({ data }) => data),
        tap((report) => {
          if (report.downloadUrl) {
            const link = document.createElement('a');
            link.style.display = 'none';
            link.download = report.document?.name!;
            link.href = report.downloadUrl;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
          }
        })
      );
  }
}
